import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Input,
  Spinner,
  Button,
} from "reactstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  superadminGetComboCompany,
  superadminGetChangeCompany,
} from "helpers/backend_helpers";

interface Company {
  _id: string;
  name: string;
}

const SuperadminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [displayCount, setDisplayCount] = useState<number>(9);

  useEffect(() => {
    const userRole = sessionStorage.getItem("userRole");
    if (userRole !== "superadmin") {
      toast.error("Akses ditolak!");
      navigate("/dashboard");
      return;
    }

    sessionStorage.removeItem("currentCompany");
    sessionStorage.removeItem("menu");

    window.dispatchEvent(new Event("sessionUpdated"));

    fetchCompanies();
  }, [navigate]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response: any = await superadminGetComboCompany();
      setCompanies(response || []);
    } catch (error) {
      toast.error("Gagal mengambil daftar perusahaan!");
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = useMemo(() => {
    if (!searchTerm.trim()) return companies;
    return companies.filter((company) =>
      company.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [companies, searchTerm]);

  const displayedCompanies = useMemo(() => {
    return filteredCompanies.slice(0, displayCount);
  }, [filteredCompanies, displayCount]);

  const handleLoadMore = () => {
    setDisplayCount((prev) => prev + 9);
  };

  const handleSwitchToCompany = async (company: Company) => {
    setSwitching(company._id);
    try {
      const response: any = await superadminGetChangeCompany(company._id);

      sessionStorage.setItem("authUser", JSON.stringify(response.access_token));

      sessionStorage.setItem("user", JSON.stringify(response.company));

      const companyData = response.company;
      console.log(companyData.adminMenus);

      if (!companyData?.adminMenus) {
        sessionStorage.setItem("menu", JSON.stringify([]));
      } else {
        let menus = [...companyData.adminMenus];
        if (companyData.allowCheckPointPatrol === true) {
          menus.push("PATROL_LOCATION");
        }
        sessionStorage.setItem("menu", JSON.stringify(menus));
      }

      sessionStorage.setItem(
        "currentCompany",
        JSON.stringify({
          _id: company._id,
          name: company.name,
        }),
      );

      toast.success(`Berhasil masuk ke ${company.name}`);
      navigate("/dashboard");

      setTimeout(() => {
        window.location.reload();
      }, 100);
    } catch (error: any) {
      toast.error(error?.message || "Gagal pindah ke perusahaan!");
      setSwitching(null);
    }
  };

  const getInitials = (name: string): string => {
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  // Reset displayCount when search term changes
  useEffect(() => {
    setDisplayCount(9);
  }, [searchTerm]);

  document.title = "Dashboard Superadmin";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row className="mb-4">
            <Col lg={12}>
              <div className="text-center py-4">
                <div className="avatar-lg mx-auto mb-3">
                  <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-24">
                    <i className="ri-building-line"></i>
                  </div>
                </div>
                <h4 className="mb-2">Selamat Datang, Superadmin</h4>
                <p className="text-muted mb-0">
                  Pilih perusahaan untuk mulai mengelola data
                </p>
              </div>
            </Col>
          </Row>

          <Row className="justify-content-center mb-4">
            <Col md={8} lg={6} xl={5}>
              <div className="search-box">
                <Input
                  type="text"
                  className="form-control search bg-light border-light"
                  placeholder="Cari perusahaan..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: "40px" }}
                />
                <i className="ri-search-line search-icon"></i>
              </div>
            </Col>
          </Row>

          {!loading && (
            <Row className="justify-content-center mb-3">
              <Col md={8} lg={6} xl={5}>
                <p className="text-muted text-center mb-0">
                  {filteredCompanies.length === companies.length ? (
                    <>
                      Menampilkan <strong>{displayedCompanies.length}</strong>{" "}
                      dari <strong>{companies.length}</strong> perusahaan
                    </>
                  ) : (
                    <>
                      Menampilkan <strong>{displayedCompanies.length}</strong>{" "}
                      dari <strong>{filteredCompanies.length}</strong> hasil
                      pencarian
                    </>
                  )}
                </p>
              </Col>
            </Row>
          )}

          {loading ? (
            <Row className="justify-content-center">
              <Col lg={12}>
                <div className="text-center py-5">
                  <Spinner
                    color="primary"
                    style={{ width: "3rem", height: "3rem" }}
                  />
                  <p className="mt-3 text-muted">Memuat daftar perusahaan...</p>
                </div>
              </Col>
            </Row>
          ) : (
            <>
              {filteredCompanies.length > 0 ? (
                <>
                  <Row className="justify-content-center">
                    <Col xl={10}>
                      <Row>
                        {displayedCompanies.map((company) => (
                          <Col md={6} lg={4} key={company._id} className="mb-4">
                            <Card
                              className="card-hover border-0 shadow-sm h-100"
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                switching === null &&
                                handleSwitchToCompany(company)
                              }
                            >
                              <CardBody className="p-4">
                                <div className="d-flex align-items-center">
                                  <div className="flex-shrink-0">
                                    <div
                                      className="avatar-md rounded-circle"
                                      style={{ backgroundColor: "#e9ecef" }}
                                    >
                                      <span className="avatar-title rounded-circle text-dark fs-18 fw-semibold">
                                        {getInitials(company.name)}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="flex-grow-1 ms-3 overflow-hidden">
                                    <h5
                                      className="mb-1 text-truncate"
                                      title={company.name}
                                    >
                                      {company.name}
                                    </h5>
                                    <p className="text-muted mb-0 small">
                                      Klik untuk masuk
                                    </p>
                                  </div>

                                  <div className="flex-shrink-0">
                                    {switching === company._id ? (
                                      <Spinner size="sm" color="primary" />
                                    ) : (
                                      <i className="ri-arrow-right-s-line fs-20 text-muted"></i>
                                    )}
                                  </div>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </Col>
                  </Row>

                  {displayedCompanies.length < filteredCompanies.length && (
                    <Row className="justify-content-center mt-3 mb-3">
                      <Col lg={12}>
                        <div className="text-center">
                          <Button
                            color="primary"
                            outline
                            onClick={handleLoadMore}
                            className="px-4"
                          >
                            <i className="ri-add-line me-1"></i>
                            Tampilkan Lebih Banyak (
                            {filteredCompanies.length -
                              displayedCompanies.length}{" "}
                            lagi)
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  )}
                </>
              ) : (
                <Row className="justify-content-center">
                  <Col md={6} lg={4}>
                    <Card className="border-0 shadow-sm">
                      <CardBody className="text-center py-5">
                        <div className="avatar-lg mx-auto mb-3">
                          <div className="avatar-title bg-light text-secondary rounded-circle fs-24">
                            <i className="ri-building-line"></i>
                          </div>
                        </div>
                        {searchTerm ? (
                          <>
                            <h5 className="mb-2">Tidak Ditemukan</h5>
                            <p className="text-muted mb-0">
                              Tidak ada perusahaan dengan nama "{searchTerm}"
                            </p>
                          </>
                        ) : (
                          <>
                            <h5 className="mb-2">Belum Ada Data</h5>
                            <p className="text-muted mb-0">
                              Belum ada perusahaan yang terdaftar
                            </p>
                          </>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}
            </>
          )}
        </Container>
      </div>

      <style>
        {`
          .card-hover {
            transition: all 0.3s ease;
          }
          .card-hover:hover {
            transform: translateY(-4px);
            box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
          }
          .card-hover:hover .ri-arrow-right-s-line {
            color: var(--vz-primary) !important;
            transform: translateX(4px);
          }
          .ri-arrow-right-s-line {
            transition: all 0.3s ease;
          }
          .search-box {
            position: relative;
          }
          .search-box .search-icon {
            position: absolute;
            left: 13px;
            top: 50%;
            transform: translateY(-50%);
            color: #878a99;
          }
          .avatar-md {
            width: 3.5rem;
            height: 3.5rem;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .avatar-lg {
            width: 4rem;
            height: 4rem;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .bg-light {
            background-color: #e9ecef !important;
          }
          .avatar-md.rounded-circle {
            background-color: #e9ecef !important;
          }
          .avatar-title {
            background-color: transparent !important;
          }
        `}
      </style>
    </React.Fragment>
  );
};

export default SuperadminDashboard;
